import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { CitationLink } from '../../components/ui/citation';
import { Paper, Stack, Title as MantineTitle, Text, Group, Anchor, Table } from '@mantine/core';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { citations, rentalMarketSampleData, IconMapping } from '../../data';

const RentalResearchPanel: React.FC = () => {
    return (
        <Stack gap="lg">
            <Card>
                <CardHeader>
                    <CardTitle>
                        <Group gap="xs">
                            <IconMapping.BarChart3 size={20} />
                            近期芳村區域商舖租金市場調研 (2025年5月)
                        </Group>
                    </CardTitle>
                </CardHeader>
                <CardContent pt="md">
                    <Stack gap="md">
                        <Text>
                            為了瞭解目前芳村區域與本報告分析物業（37.5㎡小區商舖）相似類型商舖的租金水平，
                            我們於2025年5月18日透過安居客平台 (<CitationLink id="6" callType="recommend" citations={citations}/>) 對廣州市荔灣區芳村掛牌出租的20-50平方米商舖進行了檢索，並選取了若干代表性掛牌實例。
                            觀察到的這些實例月租金報價範圍較廣，從低於1000元/月至近10000元/月不等，大部分主流租金報價集中在2000元至6500元之間。具體房源資訊（包括URL）已包含在本報告圖表的數據源中。
                        </Text>
                        <MantineTitle order={5}>調研總結：</MantineTitle>
                        <Stack gap="xs" pl="md">
                            <Text>
                                - 掛牌租金範圍較廣：觀察到的租金從每月不足1000元到近萬元不等，具體取決於多種因素。
                            </Text>
                            <Text>
                                - 主流租金區間：如上所述，30-50平方米的普通商舖，主流月租金報價多在3000元至5500元之間。
                            </Text>
                            <Text>
                                - 與報告物業對比：本報告中物業的2025年預測月租金為2050元，顯著低於市場主流掛牌租金的一般範圍。
                            </Text>
                        </Stack>
                        <MantineTitle order={5} mt="sm">初步結論：</MantineTitle>
                        <Text>
                            目前芳村區域內類似面積的商舖市場租金似乎普遍高於本報告物業的現有及預測租金水平。
                            若該物業能以市場主流價格（例如3000-4000元/月範圍）成功出租，其回報率將得到顯著改善。
                            建議業主可進一步評估提升租金的可能性，考量物業具體條件和市場推廣。
                        </Text>
                    </Stack>
                </CardContent>
            </Card>

            {/* New Card for Rental Price vs. Size Chart */}
            <Card>
                <CardHeader>
                    <CardTitle>
                        <Group gap="xs">
                            <IconMapping.BarChart3 size={20} />
                            芳村商舖租金與面積關係示意圖 (含市場掛牌示例)
                        </Group>
                    </CardTitle>
                </CardHeader>
                <CardContent pt="md">
                    <Text mb="md">
                        下圖旨在示意目前芳村部分商舖租金與面積的一般關係。其中「市場掛牌示例」來源於2025年5月18日對安居客 (<CitationLink id="6" callType="recommend" citations={citations}/>) 芳村區域掛牌資訊的公開網絡檢索，
                        這些均為具體房源，其URL已包含在圖表數據源 (rentalMarketSampleData) 中，可供查閱。圖中亦包含本報告分析物業（37.5㎡，預測月租金2050元）作為對比點。
                        請注意，實際市場租金受多種具體因素影響，波動範圍較大。
                    </Text>
                    <Paper h={350} p="sm" withBorder>
                        <ResponsiveContainer width="100%" height="100%">
                            <ScatterChart
                                margin={{
                                    top: 20, right: 30, bottom: 20, left: 20,
                                }}
                            >
                                <CartesianGrid />
                                <XAxis type="number" dataKey="size" name="面積" unit="㎡" domain={['dataMin - 5', 'dataMax + 5']} />
                                <YAxis type="number" dataKey="rent" name="月租金" unit="元" domain={['dataMin - 500', 'dataMax + 500']} />
                                <Tooltip cursor={{ strokeDasharray: '3 3' }} formatter={(value: any, name: any) => [value, name === 'size' ? '面積 (㎡)' : '月租金 (元)']}/>
                                <Legend />
                                <Scatter name="市場掛牌示例" data={rentalMarketSampleData.filter(p => p.name !== '本物業 (預測)')} fill="#8884d8" />
                                <Scatter name="本物業 (預測)" data={rentalMarketSampleData.filter(p => p.name === '本物業 (預測)')} fill="#d0ed57" shape="star" />
                            </ScatterChart>
                        </ResponsiveContainer>
                    </Paper>
                    <MantineTitle order={5} mt="lg" mb="sm">圖表解讀：</MantineTitle>
                    <Text>
                        從抽樣數據來看，芳村區域內30-50平方米的商舖月租金普遍高於本物業目前的預測租金（2050元/月）。
                        市場上的近似面積商舖，其租金報價多數落在3000元至5500元區間。
                        這進一步表明，本物業的租金水平可能存在提升空間。當然，實際租金會受到物業的具體位置、樓層、新舊程度、裝修狀況、配套設施以及市場推廣力度等多種因素影響。
                    </Text>

                    <MantineTitle order={5} mt="xl" mb="sm">圖表數據源詳情:</MantineTitle>
                    <Table striped highlightOnHover withTableBorder withColumnBorders>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th>名稱/來源</Table.Th>
                                <Table.Th ta="right">面積 (㎡)</Table.Th>
                                <Table.Th ta="right">月租金 (元)</Table.Th>
                                <Table.Th>連結</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {[...rentalMarketSampleData].sort((a, b) => a.size - b.size).map((item, index) => (
                                <Table.Tr key={index}>
                                    <Table.Td>{item.name === '本物業 (預測)' ? '本物業 (預測)' : item.name}</Table.Td>
                                    <Table.Td ta="right">{item.size.toFixed(1)}</Table.Td>
                                    <Table.Td ta="right">{item.rent.toLocaleString()}</Table.Td>
                                    <Table.Td>
                                        {item.url ? (
                                            <Anchor href={item.url} target="_blank" rel="noopener noreferrer" size="sm">
                                                查看詳情
                                            </Anchor>
                                        ) : (
                                            item.name === '本物業 (預測)' ? 'N/A (本物業)' : 'N/A'
                                        )}
                                    </Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                </CardContent>
            </Card>
        </Stack>
    );
}

export default RentalResearchPanel; 